import { useState, useEffect } from "react";
import axios from "axios";
import { type AnalysisEntry } from "@/lib/projectStore";
import { TrendingUp, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ImpactAnalysisWizard from "./ImpactAnalysisWizard";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  mongoProjectId: string;
  baProjectId: string | null;
  onAnalysisGenerated: (analysis: AnalysisEntry) => void;
}

export default function ImpactAnalysisPanel({ mongoProjectId, baProjectId, onAnalysisGenerated }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [requirements, setRequirements] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReq, setSelectedReq] = useState<any | null>(null);

  useEffect(() => {
    if (!baProjectId) return;
    setLoading(true);
    axios.get(`${BASE}/api/tools/projects/${baProjectId}`, { headers: authHeader })
      .then(({ data }) => setRequirements(data.requirements || []))
      .catch(() => setRequirements([]))
      .finally(() => setLoading(false));
  }, [baProjectId]);

  // Wizard is active — show it full-panel
  if (selectedReq) {
    return (
      <div className="h-full flex flex-col">
        <ImpactAnalysisWizard
          requirement={selectedReq}
          onConfirm={(analysis) => {
            setSelectedReq(null);
            onAnalysisGenerated(analysis);
          }}
          onClose={() => setSelectedReq(null)}
        />
      </div>
    );
  }

  // No BA project yet
  if (!baProjectId) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full gap-3 text-center">
        <TrendingUp className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-semibold text-foreground">Impact Analysis</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          Visit the Requirements tab first to initialise the project backend, then come back here to run an impact analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-2xl">
      {/* Header */}
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <TrendingUp className="h-4 w-4" /> Impact Analysis
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Select a requirement to analyse its downstream impact — affected systems, stakeholders, risks, and dependencies.
        </p>
      </div>

      {loading && (
        <p className="text-xs text-muted-foreground animate-pulse">Loading requirements…</p>
      )}

      {!loading && requirements.length === 0 && (
        <div className="rounded-md border border-border bg-secondary/20 px-4 py-6 text-center space-y-1">
          <AlertTriangle className="h-6 w-6 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No requirements yet.</p>
          <p className="text-xs text-muted-foreground">Create requirements in the Requirements tab first.</p>
        </div>
      )}

      {!loading && requirements.length > 0 && (
        <div className="space-y-2">
          {requirements.map((req) => (
            <div
              key={req.id}
              className="border border-border rounded-md bg-card p-3 flex items-start justify-between gap-3 hover:border-accent/50 transition-colors cursor-pointer group"
              onClick={() => setSelectedReq(req)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-card-foreground truncate">{req.title}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase border border-border rounded px-1.5 py-0.5">{req.req_type}</span>
                  {req.feature && <span className="text-[10px] text-muted-foreground">{req.feature}</span>}
                  {req.contains_phi && (
                    <span className="text-[10px] font-mono text-destructive bg-destructive/10 border border-destructive/20 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                      <AlertTriangle className="h-2.5 w-2.5" /> PHI
                    </span>
                  )}
                </div>
              </div>
              <button className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-muted-foreground group-hover:text-accent transition-colors border border-border group-hover:border-accent/50 rounded px-2.5 py-1">
                <TrendingUp className="h-3.5 w-3.5" /> Analyse
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
